import { useCallback, useState, type ReactNode } from 'react'
import type { MessageKey } from '@/i18n'
import type { TagEntry } from '@/features/gate/commands'
import { Btn, Keypad, Readout } from '@/features/gate/v1/kit'
import { LiveScanner, Refused, TypeCodeSheet } from '@/features/gate/v1/scan'
import { C, typeKey } from '@/features/gate/v1/style'
import { kg, tr } from '@/features/gate/v1/text'
import type { V1IconName } from '@/features/gate/v1/sprite'
import type { ProcessState } from '../projection'
import { needsSecondEntry, type Refusal } from '../model/rules'
import { expandTyped } from './model'
import { NoSiteScreen, useSite } from './ui'

/**
 * Pieces shared by the V1 processing boards (features/process/v1): the site wrapper, the
 * scanner with नंबर लिखें, the scale keypad with its second entry, and the refusal words.
 */

/** A V1 sample is filled to the mark on the bag: its standard weight (no scale at sealing). */
export const V1_QC_SAMPLE_G = 500

/** The refusal in the language on screen. */
export const refusalText = (r: Refusal | string) => tr(`process.refusal.${r}` as MessageKey)

/** The site list (pack), or the board asking to download it. */
export function WithSite({ children }: { children: (state: ProcessState) => ReactNode }) {
  const { loading, state } = useSite()
  if (!state) return <NoSiteScreen loading={loading} />
  return <>{children(state)}</>
}

/**
 * The live scanner, नंबर लिखें (a typed number is expanded for `kind`) and the red line. `onCode`
 * gets the whole code and returns the refusal to show, or null.
 */
export function PScanner({
  state,
  kind,
  hint,
  icon = 'tag',
  onCode,
  paused = false,
  extra,
}: {
  state: ProcessState
  kind: 'BAG' | 'OUTPUT_BAG' | 'QC_SEAL' | 'MACHINE'
  hint: string
  icon?: V1IconName
  onCode: (code: string, entry: TagEntry) => string | null
  /** A sheet or card is up: camera off. */
  paused?: boolean
  /** Buttons beside नंबर लिखें. */
  extra?: ReactNode
}) {
  const [err, setErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const check = (raw: string, entry: TagEntry) => {
    const code = entry === 'TYPED' ? expandTyped(state, raw, kind) : raw.trim().toUpperCase()
    return onCode(code, entry)
  }
  return (
    <>
      {typing && (
        <TypeCodeSheet
          title={tr('gate.v1.tag.type')}
          check={(raw, entry) => {
            setErr(null)
            return check(raw, entry)
          }}
          onClose={() => setTyping(false)}
        />
      )}
      {!paused && !typing && (
        <LiveScanner icon={icon} hint={hint} minHeight={160} check={check} onError={setErr} />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn icon="keypad" onClick={() => setTyping(true)}>
          {tr('gate.v1.tag.type')}
        </Btn>
        {extra}
      </div>
      {err && <Refused text={err} />}
    </>
  )
}

export interface ScaleReading {
  read_g: number
  confirmed_twice: boolean
}

/**
 * The scale reading on the keypad (sack and grain together). A reading outside the site's usual
 * range is typed a second time; the two must agree.
 */
export function useScaleEntry(state: ProcessState, tareG: number) {
  const [value, setValue] = useState('')
  const [first, setFirst] = useState<number | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const onKey = useCallback((k: string) => {
    setErr(null)
    setValue((c) => typeKey(c, k, { decimals: 2, max: 6 }))
  }, [])
  const grams = value ? Math.round(Number(value) * 1000) : 0
  const net = grams - tareG
  const reset = () => {
    setValue('')
    setFirst(null)
    setErr(null)
  }
  /** The reading when it is good to save, else null (and the screen asks again or says why). */
  const submit = (): ScaleReading | null => {
    if (!grams) return null
    if (net <= 0) {
      setErr(tr('process.v1.weigh.tooLight'))
      return null
    }
    if (first !== null) {
      if (first !== grams) {
        setFirst(null)
        setValue('')
        setErr(tr('process.v1.weigh.notSame'))
        return null
      }
      return { read_g: grams, confirmed_twice: true }
    }
    if (needsSecondEntry(grams, state.pack.settings)) {
      setFirst(grams)
      setValue('')
      setErr(tr('process.v1.weigh.again'))
      return null
    }
    return { read_g: grams, confirmed_twice: false }
  }
  return { value, grams, net, err, onKey, submit, reset }
}

/** The readout (with the sack) and the grain beside it, then the keypad. */
export function ScalePad({
  label,
  entry,
}: {
  label: string
  entry: ReturnType<typeof useScaleEntry>
}) {
  return (
    <>
      <Readout
        label={label}
        value={entry.value}
        unit={tr('process.v1.kgUnit')}
        side={
          entry.grams > 0 && entry.net > 0 ? (
            <span style={{ fontSize: 17, fontWeight: 700, color: C.ok, whiteSpace: 'nowrap' }}>
              {tr('process.v1.weigh.net', { kg: kg(entry.net) })}
            </span>
          ) : undefined
        }
      />
      {entry.err && <Refused text={entry.err} />}
      <div style={{ flexGrow: 1 }} />
      <Keypad onKey={entry.onKey} />
    </>
  )
}
